import { splitEvery } from 'ramda';

import merchantGatewayApi from 'apis/merchant-gateway/api';
import api from 'apis/restaurant-api/api';
import { ProductInformationSettings } from 'apis/restaurant-api/merchant-admin/menu2/types';
import * as apiUtils from 'apis/restaurant-api/utils';

import {
  CategoryLine,
  ImportResponse,
  OfferLine,
  OptionLine,
  ValidationResponse,
} from './pages/GoogleSheetsImportPage/libs/googleSheets/types';
import { CreateOptionPayload, UpdateOptionPayload } from './pages/SharedMenuEditorPage/components/OptionForm';
import {
  FullSelection,
  CreateCategoryPayload,
  EditCategoryPayload,
  Offer,
  Option,
  OptionBinding,
  OptionBindingAttrs,
} from './pages/SharedMenuEditorPage/types';
import {
  CreateSharedMenuPayload,
  OfferingPlatformScope,
  SharedMenu,
  VenueLinksResponse,
  BatchCategoryChangePayload,
  BatchCreateSubcategoriesPayload,
  SelectionBootstrappingStatusResponse,
  SelectionCategory,
  DuplicateSharedMenuPayload,
  EditSharedMenuInfoPayload,
  OverrideSettingsResponse,
  UpdateOfferOverridesPayload,
  SelectionOverridesResponse,
  SelectionPublishResponse,
  OfferingApiUpdateOffersResponse,
  OfferingApiUpdateOffersPayload,
  OfferingApiDeleteOffersPayload,
  OfferingApiDeleteOffersResponse,
  VenueBootstrapStatusResponse,
  OfferInventoryDataResponse,
  OfferingApiBatchUpdateOverridesPayload,
  OfferingApiBatchUpdateOverridesResponse,
} from './types';

export const OFFERING_PLATFORM = '/offering-platform';
export const OFFERING_API = `${OFFERING_PLATFORM}/offering-api`;

export const createSharedMenu = async (payload: CreateSharedMenuPayload) =>
  merchantGatewayApi.post<SharedMenu>(`${OFFERING_PLATFORM}/selections`, payload).then(response => response.data);

export const deleteSharedMenuDraft = async (selectionId: string) =>
  merchantGatewayApi.delete(`${OFFERING_PLATFORM}/selections/${selectionId}/draft`).then(response => response.data);

export const duplicateSharedMenu = async (selectionId: string, payload: DuplicateSharedMenuPayload) =>
  merchantGatewayApi
    .post<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/clone`, payload)
    .then(response => response.data);

export const getSharedMenus = async (scope: OfferingPlatformScope) =>
  merchantGatewayApi
    .get<SharedMenu[]>(`${OFFERING_PLATFORM}/selections`, { params: { scope_type: scope.type, scope_id: scope.id } })
    .then(response => response.data);

export const linkVenuesToSharedMenu = async (selectionId: string, venueIds: string[]) =>
  merchantGatewayApi
    .post(`${OFFERING_PLATFORM}/selections/${selectionId}/link`, { venue_ids: venueIds })
    .then(response => response.data);

export const unlinkVenuesFromSharedMenu = async (selectionId: string, venueIds: string[]) =>
  merchantGatewayApi
    .post(`${OFFERING_PLATFORM}/selections/${selectionId}/unlink`, { venue_ids: venueIds })
    .then(response => response.data);

export const getVenueLinks = async (venueIds: string[]) => {
  const idChunks = splitEvery(50, venueIds);

  const allResults = await Promise.all(
    idChunks.map(ids =>
      merchantGatewayApi.get<VenueLinksResponse>(
        `${OFFERING_PLATFORM}/venue-links?${ids.map(id => `venue_id=${id}&`).join('')}`,
      ),
    ),
  );

  return {
    venue_links: allResults.flatMap(result => result.data.venue_links),
  };
};

export const getSelectionAll = async (selectionId: string, includeDraft: boolean) =>
  merchantGatewayApi
    .get<FullSelection>(`${OFFERING_PLATFORM}/selections/${selectionId}/all?include_draft=${includeDraft}`)
    .then(response => response.data);

export const createCategory = async (selectionId: string, payload: CreateCategoryPayload) =>
  merchantGatewayApi
    .post<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/categories`, payload)
    .then(response => response.data);

export const deleteCategory = async (selectionId: string, categoryId: string) =>
  merchantGatewayApi
    .delete<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/categories/${categoryId}`)
    .then(response => response.data);

export const updateCategory = async (selectionId: string, categoryId: string, payload: EditCategoryPayload) =>
  merchantGatewayApi
    .put<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/categories/${categoryId}`, payload)
    .then(response => response.data);

export const updateCategoryOrder = async (selectionId: string, categoryIds: string[]) =>
  merchantGatewayApi
    .put<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/categories/order`, { category_ids: categoryIds })
    .then(response => response.data);

export const publishSharedMenu = async (selectionId: string, publishAt: string | null) =>
  merchantGatewayApi
    .post<SelectionPublishResponse>(`${OFFERING_PLATFORM}/v2/selections/${selectionId}/publish`, {
      publishAt,
    })
    .then(response => response.data);

export const deletePublishRequest = async (selectionId: string) =>
  merchantGatewayApi
    .delete<SelectionPublishResponse>(`${OFFERING_PLATFORM}/v2/selections/${selectionId}/publish`)
    .then(response => response.data);

export const updateSharedMenuInfo = async (selectionId: string, payload: EditSharedMenuInfoPayload) =>
  merchantGatewayApi
    .put<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}`, { ...payload })
    .then(response => response.data);

export const createOffer = async (selectionId: string, payload: any) =>
  merchantGatewayApi
    .post<Offer>(`${OFFERING_PLATFORM}/selections/${selectionId}/offers`, payload)
    .then(response => response.data);

export const updateOffer = async (selectionId: string, offerId: string, payload: any) =>
  merchantGatewayApi
    .put<Offer>(`${OFFERING_PLATFORM}/selections/${selectionId}/offers/${offerId}`, payload)
    .then(response => response.data);

export const deleteOffer = async (selectionId: string, offerId: string) =>
  merchantGatewayApi
    .delete(`${OFFERING_PLATFORM}/selections/${selectionId}/offers/${offerId}`)
    .then(response => response.data);

export const createOfferBinding = (selectionId: string, categoryId: string, offerId: string) =>
  merchantGatewayApi
    .post<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/categories/${categoryId}/offer-bindings`, {
      offer_id: offerId,
    })
    .then(response => response.data);

export const deleteOfferBinding = (selectionId: string, categoryId: string, offerId: string) =>
  merchantGatewayApi
    .delete<SharedMenu>(
      `${OFFERING_PLATFORM}/selections/${selectionId}/categories/${categoryId}/offer-bindings/${offerId}`,
    )
    .then(response => response.data);

export const updateOfferBindingsOrder = (selectionId: string, categoryId: string, offerIds: string[]) =>
  merchantGatewayApi
    .put<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/categories/${categoryId}/offer-bindings/order`, {
      offer_ids: offerIds,
    })
    .then(response => response.data);

export const fetchImageUrls = (filename: string, extraData: Record<string, any> = {}) =>
  api
    .post<{
      cdn_url: string;
      upload_url: string;
    }>('/v2/merchant-admin/menus/upload-image', {
      original_filename: filename,
      ...extraData,
    })
    .then(response => response.data);

export const uploadSelectionImage = async (file: File, extraData: Record<string, any> = {}) => {
  const { cdn_url, upload_url } = await fetchImageUrls(file.name, extraData);
  await apiUtils.uploadFileToUrl(file, upload_url);
  return cdn_url;
};

export const createOption = async (selectionId: string, payload: CreateOptionPayload) =>
  merchantGatewayApi
    .post<Option>(`${OFFERING_PLATFORM}/selections/${selectionId}/options`, payload)
    .then(response => response.data);

export const updateOption = async (selectionId: string, optionId: string, payload: UpdateOptionPayload) =>
  merchantGatewayApi
    .put<Option>(`${OFFERING_PLATFORM}/selections/${selectionId}/options/${optionId}`, payload)
    .then(response => response.data);

export const deleteOption = async (selectionId: string, optionId: string) =>
  merchantGatewayApi
    .delete<FullSelection>(`${OFFERING_PLATFORM}/selections/${selectionId}/options/${optionId}`)
    .then(response => response.data);

export const deleteOptionChoice = async (selectionId: string, optionId: string, optionChoiceId: string) =>
  merchantGatewayApi
    .delete<Option>(`${OFFERING_PLATFORM}/selections/${selectionId}/options/${optionId}/values/${optionChoiceId}`)
    .then(response => response.data);

export const createOptionBinding = async (selectionId: string, offerId: string, payload: OptionBindingAttrs) =>
  merchantGatewayApi
    .post<Offer>(`${OFFERING_PLATFORM}/selections/${selectionId}/offers/${offerId}/option-bindings`, payload)
    .then(response => response.data);

export const updateOptionBinding = async (
  selectionId: string,
  offerId: string,
  optionBindingId: string,
  payload: OptionBinding,
) =>
  merchantGatewayApi
    .put<Offer>(
      `${OFFERING_PLATFORM}/selections/${selectionId}/offers/${offerId}/option-bindings/${optionBindingId}`,
      payload,
    )
    .then(response => response.data);

export const deleteOptionBinding = async (selectionId: string, offerId: string, optionBindingId: string) =>
  merchantGatewayApi
    .delete<Offer>(
      `${OFFERING_PLATFORM}/selections/${selectionId}/offers/${offerId}/option-bindings/${optionBindingId}`,
    )
    .then(response => response.data);

export const getProductInformationSettings = async (): Promise<ProductInformationSettings> =>
  api
    .get<ProductInformationSettings>('/v2/merchant-admin/menus/product-information-settings')
    .then(response => response.data);

export const batchChangeCategory = (selectionId: string, payload: BatchCategoryChangePayload) =>
  merchantGatewayApi
    .post<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/offers/batch-change-category`, payload)
    .then(response => response.data);

export const batchCreateSubcategories = (
  selectionId: string,
  categoryId: string,
  payload: BatchCreateSubcategoriesPayload[],
) =>
  merchantGatewayApi
    .post<SharedMenu>(
      `${OFFERING_PLATFORM}/selections/${selectionId}/categories/${categoryId}/bulk-create-subcategories`,
      { subcategories: payload },
    )
    .then(response => response.data);

export const updateSubcategoryOrder = (selectionId: string, categoryId: string, subcategoryIds: string[]) =>
  merchantGatewayApi
    .put<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/categories/${categoryId}/order-subcategories`, {
      category_ids: subcategoryIds,
    })
    .then(response => response.data);

export const changeSubcategoryParentCategory = (selectionId: string, subcategoryId: string, categoryId: string) =>
  merchantGatewayApi.put<SharedMenu>(
    `${OFFERING_PLATFORM}/selections/${selectionId}/categories/${subcategoryId}/assign-parent-category`,
    {
      parent_category_id: categoryId,
    },
  );

export const startSelectionBootstrapping = (selectionId: string, sourceVenueId: string) =>
  merchantGatewayApi
    .post<SharedMenu>(`${OFFERING_PLATFORM}/selections/${selectionId}/bootstrap`, { venue_id: sourceVenueId })
    .then(response => response.data);

export const resetSelectionBootstrapping = (selectionId: string) =>
  merchantGatewayApi.delete(`${OFFERING_PLATFORM}/selections/${selectionId}/bootstrap`).then(response => response.data);

export const getSelectionBootstrappingStatus = (selectionId: string) =>
  merchantGatewayApi
    .get<SelectionBootstrappingStatusResponse>(`${OFFERING_PLATFORM}/selections/${selectionId}/bootstrapping-status`)
    .then(response => response.data);

export const batchValidatePartialOffers = (selectionId: string, offers: Partial<OfferLine>[], offset: number) =>
  merchantGatewayApi
    .patch<ValidationResponse>(
      `${OFFERING_PLATFORM}/selections/${selectionId}/offers/validate-batch?line_shift=${offset}`,
      { offers },
    )
    .then(response => response.data);

export const batchValidatePartialOptions = (selectionId: string, options: Partial<OptionLine>[]) =>
  merchantGatewayApi
    .patch<ValidationResponse>(`${OFFERING_PLATFORM}/selections/${selectionId}/options/validate-batch`, { options })
    .then(response => response.data);

export const batchValidatePartialCategories = (selectionId: string, categories: Partial<CategoryLine>[]) =>
  merchantGatewayApi
    .patch<ValidationResponse>(`${OFFERING_PLATFORM}/selections/${selectionId}/categories/validate-batch`, {
      categories,
    })
    .then(response => response.data);

export const batchImportPartialOffers = (selectionId: string, offers: Partial<OfferLine>[], offset: number) =>
  merchantGatewayApi
    .patch<ImportResponse<Offer>>(`${OFFERING_PLATFORM}/selections/${selectionId}/offers/batch?line_shift=${offset}`, {
      offers,
    })
    .then(response => response.data);

export const batchImportPartialOptions = (selectionId: string, options: Partial<OptionLine>[]) =>
  merchantGatewayApi
    .patch<ImportResponse<Option>>(`${OFFERING_PLATFORM}/selections/${selectionId}/options/batch`, { options })
    .then(response => response.data);

export const batchImportPartialCategories = (selectionId: string, categories: Partial<CategoryLine>[]) =>
  merchantGatewayApi
    .patch<ImportResponse<SelectionCategory>>(`${OFFERING_PLATFORM}/selections/${selectionId}/categories/batch`, {
      categories,
    })
    .then(response => response.data);

export const deleteSharedMenu = (selectionId: string) =>
  merchantGatewayApi.delete(`${OFFERING_PLATFORM}/selections/${selectionId}`).then(response => response.data);

export const getOverrideSettings = (scope: OfferingPlatformScope) =>
  merchantGatewayApi
    .get<OverrideSettingsResponse>(`${OFFERING_PLATFORM}/setting-groups/menu_sharing`, {
      params: { scope_type: scope.type, scope_id: scope.id },
    })
    .then(response => response.data);

export const updateOverrideSettings = (payload: OverrideSettingsResponse) =>
  merchantGatewayApi.patch(`${OFFERING_PLATFORM}/setting-groups/menu_sharing`, payload).then(response => response.data);

export const getSelectionOverrides = (selectionId: string, scope: OfferingPlatformScope) =>
  merchantGatewayApi
    .get<SelectionOverridesResponse>(`${OFFERING_PLATFORM}/selections/${selectionId}/overrides`, {
      params: { scope_type: scope.type, scope_id: scope.id },
    })
    .then(response => response.data);

export const updateOfferOverrides = (selectionId: string, offerId: string, payload: UpdateOfferOverridesPayload) =>
  merchantGatewayApi
    .put<Offer>(`${OFFERING_PLATFORM}/selections/${selectionId}/offers/${offerId}/overrides`, payload)
    .then(response => response.data);

export const offeringApiUpdateOffers = (payload: OfferingApiUpdateOffersPayload) =>
  merchantGatewayApi
    .put<OfferingApiUpdateOffersResponse>(`${OFFERING_API}/offers`, payload)
    .then(response => response.data);

export const offeringApiDeleteOffers = (payload: OfferingApiDeleteOffersPayload, idsToDelete: string[]) =>
  merchantGatewayApi
    .delete<OfferingApiDeleteOffersResponse>(`${OFFERING_API}/offers?id=${idsToDelete.join('&id=')}`, { data: payload })
    .then(response => response.data);

export const offeringApiBatchUpdateOverrides = (payload: OfferingApiBatchUpdateOverridesPayload) =>
  merchantGatewayApi
    .put<OfferingApiBatchUpdateOverridesResponse>(`${OFFERING_API}/offer-overrides`, payload)
    .then(response => response.data);

export const startVenueBootstrap = async (venueId: string) =>
  merchantGatewayApi.put(`${OFFERING_PLATFORM}/venue-bootstraps/${venueId}`).then(response => response.data);

export const getVenueBootstrapStatus = async (venueId: string) =>
  merchantGatewayApi
    .get<VenueBootstrapStatusResponse>(`${OFFERING_PLATFORM}/venue-bootstraps/${venueId}`)
    .then(response => response.data);

export const getVenueInventory = (venueId: string) =>
  merchantGatewayApi
    .get<OfferInventoryDataResponse[]>(`${OFFERING_PLATFORM}/offer-inventories?venue_id=${venueId}`)
    .then(response => {
      return response.data;
    });

export const updateVenueInventory = (venue_id: string, total: number, offer_id: string) =>
  merchantGatewayApi
    .put<OfferInventoryDataResponse[]>(`${OFFERING_PLATFORM}/offer-inventories/${offer_id}`, { venue_id, total })
    .then(response => {
      return response.data;
    });
